import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { faCheck, faRotateRight } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import { Spinner } from "@/components/Spinner/Spinner";
import {
	SceneArtifact,
	SceneArtifactKind,
	SceneArtifactState,
	StoryboardScene,
} from "@/pages/Private/redux/media/videoWizard.schema";
import { classNames } from "@/utils/classNames";

import { failureTextKey } from "../wizard.utils";

interface SceneCardProps {
	scene: StoryboardScene;
	index: number;
	/** Which artifact this card is about — step 2 shows the image, step 4 the video. */
	kind: SceneArtifactKind;
	artifact?: SceneArtifact | null;
	/** A job for this scene is in flight; the server, not the card, decides that. */
	isGenerating?: boolean;
	/** 0–100 from the job in the aggregate, refreshed by the socket. */
	progress?: number;
	disabled?: boolean;
	regenerateLabel: string;
	onRegenerate: (feedback?: string) => void;
	/**
	 * Which i18n block the card's own words come from. Step 2 and step 4 render the same
	 * shape but do not say the same things — a generated image is "✓ generiert", a
	 * generated video is "Prüfung offen" until a person has watched it.
	 */
	i18nScope?: string;
	/** What this artifact has cost across its renders. Omitted where the step does not show it. */
	costLabel?: string;
	/**
	 * The per-scene approval — step 4's "✓ Szene freigeben", and the only approval in the
	 * wizard that is not a whole step. Absent for step 2, whose images are approved as a
	 * batch, which is what keeps that pane unchanged.
	 */
	approveLabel?: string;
	onApprove?: () => void;
	/**
	 * Offers the finished artifact for keeping — BCM360-330. A label rather than a flag,
	 * matching the other optional affordances here: a step that has nothing to offer
	 * passes nothing and the card is unchanged.
	 */
	downloadLabel?: string;
	/** What the download label triggers — the step owns the fetch, the card only offers it. */
	onDownload?: () => void;
}

/**
 * One scene's generated artifact, with everything the author needs to iterate on it: the
 * frame and its state, the version the artifact is on, the scene's own text, and a
 * feedback field feeding the regenerate right next to it.
 *
 * Shared by step 2 (`IMAGE`) and step 4 (`VIDEO`) — the states, the version badge and the
 * per-scene feedback loop are the same thing in both, only the artifact differs.
 */
export const SceneCard: FunctionComponent<SceneCardProps> = ({
	scene,
	index,
	kind,
	artifact,
	isGenerating,
	progress,
	disabled,
	regenerateLabel,
	onRegenerate,
	i18nScope = "steps.style",
	costLabel,
	approveLabel,
	onApprove,
	downloadLabel,
	onDownload,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) => t(`videoWizard.${key}`, options ?? {}),
		[t]
	);

	const [feedback, setFeedback] = useState("");

	/**
	 * One instruction, not a stored setting: the box empties once the server has recorded
	 * what was asked for, so the next iteration starts from a blank field instead of
	 * re-sending the last request to whoever presses regenerate again without looking.
	 * What the running job was actually given is shown in its place while it runs.
	 *
	 * Keyed on the artifact's own feedback rather than cleared on submit, because that is
	 * the value that moves only once the write succeeded — a refused or failed call leaves
	 * the author's text where they typed it.
	 */
	useEffect(() => {
		setFeedback("");
	}, [artifact?.feedback]);

	const state = artifact?.state ?? SceneArtifactState.NONE;
	const isBusy = isGenerating || state === SceneArtifactState.GENERATING;
	const isStale = state === SceneArtifactState.STALE;
	const isApproved = state === SceneArtifactState.APPROVED;
	const hasArtifact = !!artifact?.assetUrl && !isBusy;
	/**
	 * Why the newest job for this scene failed. A refusal is worded here from its code, in
	 * the UI's language, because the author has to act on it; any other failure shows the
	 * API's text.
	 */
	const failureKey = failureTextKey(artifact?.lastErrorCode);
	const failureText =
		artifact?.lastError || failureKey
			? failureKey
				? ts(failureKey)
				: artifact?.lastError
			: undefined;
	/**
	 * A card with nothing on it and no explanation reads as "not started yet", which is the
	 * one thing a failed render is not — so with no asset the failure takes the media area.
	 */
	const failure = !isBusy && !artifact?.assetUrl ? failureText : undefined;
	/**
	 * With an older asset still on the card, a failed attempt used to leave no trace once
	 * the toast was gone (BCM360-374): the card showed the old clip as if nothing had been
	 * tried. The failure is a line under the media instead, and it goes once the author
	 * keeps the old asset by approving it (decided 2026-09-30).
	 */
	const failedAttempt = !isBusy && !!artifact?.assetUrl && !isApproved ? failureText : undefined;

	return (
		<div className="flex flex-col overflow-hidden rounded-card border border-border-soft bg-white">
			<div className="relative aspect-video w-full bg-surface">
				{hasArtifact &&
					(kind === SceneArtifactKind.VIDEO ? (
						<video
							controls
							className="video-own-size h-full w-full object-cover"
							src={artifact.assetUrl ?? undefined}
						/>
					) : (
						<img
							alt={scene.title}
							className="h-full w-full object-cover"
							src={artifact.assetUrl ?? undefined}
						/>
					))}

				{isBusy && (
					<div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
						<Spinner />
						<span className="text-xs font-bold text-subtle">
							{ts(`${i18nScope}.sceneGenerating`)}
						</span>
						{progress !== undefined && progress > 0 && (
							<span className="text-xs font-semibold text-subtle">{progress} %</span>
						)}
					</div>
				)}

				{!isBusy && !artifact?.assetUrl && !failure && (
					<div className="absolute inset-0 flex items-center justify-center px-3 text-center text-xs text-subtle">
						{ts(`${i18nScope}.sceneEmpty`)}
					</div>
				)}

				{!!failure && (
					<div className="absolute inset-0 flex items-center justify-center bg-error-light px-3 text-center text-xs font-semibold text-text">
						{failure}
					</div>
				)}

				{!isBusy && !!artifact?.assetUrl && (
					<span
						className={classNames(
							"absolute right-2 top-2 rounded-full px-2 py-0.5 text-xs font-semibold",
							// A step that approves its scenes one at a time has a third state:
							// rendered but not yet watched, which is a waiting look rather than
							// a finished one. Step 2 has no such state and is unchanged.
							isStale || (!!onApprove && !isApproved)
								? "bg-status-running-light text-warning-ink"
								: "bg-success-light text-text"
						)}
					>
						{isStale
							? ts(`${i18nScope}.sceneStale`)
							: onApprove && isApproved
								? ts(`${i18nScope}.sceneApproved`)
								: ts(`${i18nScope}.sceneReady`)}
					</span>
				)}

				<span className="absolute bottom-2 left-2 rounded-full bg-ink px-2 py-0.5 text-xs font-semibold text-white">
					{ts(`${i18nScope}.sceneNumber`, {
						number: index + 1,
						seconds: scene.durationSeconds,
					})}
				</span>
			</div>

			<div className="flex min-w-0 flex-1 flex-col gap-2 p-3">
				{!!failedAttempt && (
					<p
						className="rounded-card bg-error-light px-2.5 py-1.5 text-xs font-semibold text-text"
						role="alert"
					>
						{failedAttempt}
					</p>
				)}

				<div className="flex flex-wrap items-center gap-2">
					<span className="min-w-0 flex-1 truncate text-sm font-bold text-ink">{scene.title}</span>
					{!!artifact?.version && (
						<span className="shrink-0 rounded-full bg-surface px-2 py-0.5 text-xs font-semibold text-subtle">
							v{artifact.version}
						</span>
					)}
					{/*
					 * A real download, not a link: the asset bucket is another origin without
					 * CORS, where a link can only open a tab. The step fetches the bytes
					 * through the API's download endpoint instead and saves them (BCM360-330).
					 */}
					{!!downloadLabel && !!onDownload && hasArtifact && (
						<button
							className="shrink-0 rounded-full bg-surface px-2 py-0.5 text-xs font-semibold text-ink underline"
							type="button"
							onClick={onDownload}
						>
							{downloadLabel}
						</button>
					)}
				</div>

				{!!costLabel && !isBusy && (
					<span className="w-fit rounded-full bg-surface px-2 py-0.5 text-xs font-semibold text-subtle">
						{costLabel}
					</span>
				)}

				{!!scene.description && <p className="text-xs text-subtle">{scene.description}</p>}

				{/*
				 * While the render is running there is nothing to type into — the field was
				 * only ever disabled, which says "not now" without saying why. What the
				 * author wants to see instead is the change they asked for, still being
				 * worked on. The text is the artifact's own, so it is what this render is
				 * actually using rather than an unsent draft in the box.
				 */}
				{isBusy ? (
					<p className="mt-auto pt-1 text-xs text-subtle" title={artifact?.feedback ?? undefined}>
						{artifact?.feedback ? (
							<>
								{ts(`${i18nScope}.sceneRegeneratingWith`)}{" "}
								<span className="line-clamp-2 font-semibold text-text">{artifact.feedback}</span>
							</>
						) : (
							ts(`${i18nScope}.sceneGenerating`)
						)}
					</p>
				) : (
					<div className="mt-auto flex flex-wrap items-center gap-2 pt-1">
						<input
							aria-label={ts(`${i18nScope}.sceneFeedbackLabel`, { number: index + 1 })}
							className="min-w-[140px] flex-1 rounded-input border border-border bg-white px-2.5 py-1.5 text-xs text-text outline-none focus:border-accent"
							disabled={disabled}
							placeholder={ts(`${i18nScope}.sceneFeedbackPlaceholder`)}
							value={feedback}
							onChange={(event) => setFeedback(event.target.value)}
						/>
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							disabled={disabled}
							image={<Icon className="mr-2" icon={faRotateRight} />}
							size={ButtonSize.S}
							title={regenerateLabel}
							type="button"
							onClick={() => onRegenerate(feedback.trim() || undefined)}
						/>
					</div>
				)}

				{/* Only offered once there is something to watch, and only until it is approved. */}
				{!!onApprove && !!approveLabel && hasArtifact && !isApproved && (
					<Button
						className="w-full"
						color={ButtonColor.ACTION}
						disabled={disabled || isBusy}
						image={<Icon className="mr-2" icon={faCheck} />}
						size={ButtonSize.S}
						title={approveLabel}
						type="button"
						onClick={onApprove}
					/>
				)}
			</div>
		</div>
	);
};
